<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { subscribeSchemaSSE } from '../lib/schema-sse';
  import AppShell from '../components/AppShell.svelte';
  import Icon from '../components/Icon.svelte';
  import { tableIconName } from '../lib/icons';
  import { builtInTablesStore } from '../lib/custom-tables-store';
  import StatusBadge from '../components/StatusBadge.svelte';
  import Avatar from '../components/Avatar.svelte';
  import { fetchPersons, type PersonListItem } from '../lib/persons-api';
  import { bulkDeletePersons, createPerson, fetchPersonIds, fetchSavedFilters, updatePerson, type SavedFilter } from '../lib/admin-api';
  import { push } from 'svelte-spa-router';
  import { ApiError } from '../lib/api';
  import { fmtInt, fmtRelative, fmtUah } from '../lib/format';
  import Pagination from '../components/Pagination.svelte';
  import { session } from '../lib/session';
  import FilterBuilder from '../components/FilterBuilder.svelte';
  import {
    emptyGroup,
    countActive,
    fieldsForScope,
    apiFieldToFilterDef,
    type Group,
    type FieldDef,
  } from '../lib/filter-tree';
  import { fetchPublicDataTable, reorderDataFields, type ApiField } from '../lib/data-tables-api';
  import CellEdit from '../components/CellEdit.svelte';
  import BulkEditModal from '../components/BulkEditModal.svelte';
  import ColumnVisibilityMenu from '../components/ColumnVisibilityMenu.svelte';
  import ViewPresetsMenu from '../components/ViewPresetsMenu.svelte';
  import ExportHistoryButton from '../components/ExportHistoryButton.svelte';
  import ViewModeToggle from '../components/ViewModeToggle.svelte';
  import KanbanBoard from '../components/KanbanBoard.svelte';
  import KanbanCardConfigModal from '../components/KanbanCardConfigModal.svelte';
  import KanbanBulkBar from '../components/KanbanBulkBar.svelte';
  import { groupableFields as kanbanGroupableFields, fieldSupportsDrag } from '../lib/kanban-grouping';
  import { fetchKanbanFacets, fetchKanbanColumnRaw, fetchKanbanColumnIds, type KanbanSource } from '../lib/kanban-api';
  import type { ApiRecord } from '../lib/data-tables-api';
  import { loadCardConfig, saveCardConfig, loadColumnWidths, saveColumnWidths, type KanbanCardConfig, type KanbanGranularity } from '../lib/kanban-config';
  import { canViewField, canEditField, type Role } from '../lib/roles';
  import { generateCsv, downloadCsv, columnsFromFields } from '../lib/csv-export';
  import { loadState, saveState } from '../lib/persisted-state';
  import type { ApiTableView } from '../lib/data-tables-api';

  // -- Persisted UI state (відновлюється з sessionStorage на mount) --------
  // Один ключ-shape для всіх фільтрів сторінки. Restore відбувається ОДРАЗУ
  // (до решти $state), щоб уникнути зайвого load() з дефолтами.
  interface PersistedState {
    search: string;
    status: string;
    tariff: string;
    minLtvCents: number | null;
    maxLtvCents: number | null;
    minPayments: number | null;
    maxPayments: number | null;
    firstSeenFrom: string;
    firstSeenTo: string;
    lastSeenFrom: string;
    lastSeenTo: string;
    hasTelegram: boolean | null;
    hasEmail: boolean | null;
    hasPhone: boolean | null;
    sort: SortValue;
    page: number;
    pageSize: number;
    appliedTree: Group;
    viewMode: 'list' | 'kanban';
    kanbanFieldKey: string | null;
    granularity?: KanbanGranularity;
  }
  /** Sort value: built-in pattern `<col>Desc|Asc` або custom `custom:<fieldKey>:asc|desc`. */
  type SortValue =
    | 'firstSeenDesc'
    | 'firstSeenAsc'
    | 'lastSeenDesc'
    | 'lastSeenAsc'
    | 'subscriptionEndsDesc'
    | 'subscriptionEndsAsc'
    | 'ltvDesc'
    | 'ltvAsc'
    | 'paymentsDesc'
    | 'paymentsAsc'
    | `custom:${string}:asc`
    | `custom:${string}:desc`;
  const persistedDefault: PersistedState = {
    search: '',
    status: '',
    tariff: '',
    minLtvCents: null,
    maxLtvCents: null,
    minPayments: null,
    maxPayments: null,
    firstSeenFrom: '',
    firstSeenTo: '',
    lastSeenFrom: '',
    lastSeenTo: '',
    hasTelegram: null,
    hasEmail: null,
    hasPhone: null,
    sort: 'firstSeenDesc',
    page: 1,
    pageSize: 10,
    appliedTree: emptyGroup(),
    viewMode: 'list',
    kanbanFieldKey: null,
    granularity: 'month',
  };
  const restored = loadState<PersistedState>('persons-list', persistedDefault);

  let items = $state<PersonListItem[]>([]);
  let total = $state(0);
  let loading = $state(true);

  // Live search.
  let search = $state(restored.search);

  // Applied filters (commit-on-apply) — restored з sessionStorage.
  let status = $state(restored.status);
  let tariff = $state(restored.tariff);
  let minLtvCents = $state<number | null>(restored.minLtvCents);
  let maxLtvCents = $state<number | null>(restored.maxLtvCents);
  let minPayments = $state<number | null>(restored.minPayments);
  let maxPayments = $state<number | null>(restored.maxPayments);
  let firstSeenFrom = $state(restored.firstSeenFrom);
  let firstSeenTo = $state(restored.firstSeenTo);
  let lastSeenFrom = $state(restored.lastSeenFrom);
  let lastSeenTo = $state(restored.lastSeenTo);
  // Tri-state: null = ignore, true = має, false = не має.
  let hasTelegram = $state<boolean | null>(restored.hasTelegram);
  let hasEmail = $state<boolean | null>(restored.hasEmail);
  let hasPhone = $state<boolean | null>(restored.hasPhone);
  let sort = $state<SortValue>(restored.sort);

  // Pagination state.
  let page = $state(restored.page);
  let pageSize = $state(restored.pageSize);

  // Selection state. Set реасайнимо на кожну мутацію — інакше $state не
  // тригерить ререндер. Це не дорого: max 10k IDs.
  let selected = $state<Set<string>>(new Set());
  let confirmOpen = $state(false);
  let confirmBusy = $state(false);
  let confirmErr = $state<string | null>(null);

  /** Bulk-edit modal — застосовує одне значення до всіх selected рядків.
   *  Логіка винесена у <BulkEditModal>; тут тільки open-state + per-id handler. */
  let bulkEditOpen = $state(false);

  // "+ Новий запис" dropdown + modal.
  let actionsMenuOpen = $state(false);
  let createOpen = $state(false);
  let createBusy = $state(false);
  let createErr = $state<string | null>(null);
  let nDisplayName = $state('');
  let nUsername = $state('');
  let nEmail = $state('');
  let nPhone = $state('');
  let nTelegramId = $state('');

  function resetCreateForm() {
    nDisplayName = '';
    nUsername = '';
    nEmail = '';
    nPhone = '';
    nTelegramId = '';
    createErr = null;
  }
  async function submitCreatePerson(e: SubmitEvent) {
    e.preventDefault();
    createBusy = true;
    createErr = null;
    try {
      const r = await createPerson({
        displayName: nDisplayName.trim() || undefined,
        username: nUsername.trim() || undefined,
        email: nEmail.trim() || undefined,
        phone: nPhone.trim() || undefined,
        telegramId: nTelegramId.trim() || undefined,
      });
      createOpen = false;
      resetCreateForm();
      push(`/persons/${r.id}`);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'person.duplicate') {
        const details = (e.details as { matchedId?: string; fieldKeys?: string[] } | undefined) ?? {};
        const fks = details.fieldKeys ?? [];
        const labels = fks
          .map((fk) => fk === 'primaryEmail' ? 'Email' : fk === 'primaryPhone' ? 'Телефон' : fk === 'sendpulseContactId' ? 'SendPulse ID' : fk === 'telegramId' ? 'Telegram ID' : fk)
          .join(' + ');
        const link = details.matchedId ? `\n\n→ #/persons/${details.matchedId}` : '';
        createErr = `${e.message}\nКонфлікт по полях: ${labels}${link}`;
      } else {
        createErr = e instanceof Error ? e.message : String(e);
      }
    } finally {
      createBusy = false;
    }
  }

  const isAdmin = $derived($session?.role === 'ADMIN');
  const allOnPageSelected = $derived(
    items.length > 0 && items.every((i) => selected.has(i.id)),
  );

  function toggleRow(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    selected = next;
  }
  function togglePage() {
    const next = new Set(selected);
    if (allOnPageSelected) {
      for (const i of items) next.delete(i.id);
    } else {
      for (const i of items) next.add(i.id);
    }
    selected = next;
  }
  function clearSelection() {
    selected = new Set();
  }
  async function selectAllUnderFilter() {
    const p = new URLSearchParams();
    if (status) p.set('status', status);
    if (tariff) p.set('tariff', tariff);
    if (search) p.set('search', search);
    if (minLtvCents !== null) p.set('minLtvCents', String(minLtvCents));
    if (maxLtvCents !== null) p.set('maxLtvCents', String(maxLtvCents));
    if (minPayments !== null) p.set('minPayments', String(minPayments));
    if (maxPayments !== null) p.set('maxPayments', String(maxPayments));
    if (firstSeenFrom) p.set('firstSeenFrom', firstSeenFrom);
    if (firstSeenTo) p.set('firstSeenTo', firstSeenTo);
    if (lastSeenFrom) p.set('lastSeenFrom', lastSeenFrom);
    if (lastSeenTo) p.set('lastSeenTo', lastSeenTo);
    if (hasTelegram !== null) p.set('hasTelegram', String(hasTelegram));
    if (hasEmail !== null) p.set('hasEmail', String(hasEmail));
    if (hasPhone !== null) p.set('hasPhone', String(hasPhone));
    if (treeFilterCount > 0) p.set('filter', JSON.stringify(appliedTree));
    const r = await fetchPersonIds(p.toString());
    const next = new Set(selected);
    for (const id of r.ids) next.add(id);
    selected = next;
  }
  async function doDelete() {
    confirmBusy = true;
    confirmErr = null;
    try {
      const ids = Array.from(selected);
      const batches: string[][] = [];
      for (let i = 0; i < ids.length; i += 1000) batches.push(ids.slice(i, i + 1000));
      let totalDeleted = 0;
      for (const batch of batches) {
        const r = await bulkDeletePersons(batch);
        totalDeleted += r.deleted;
      }
      clearSelection();
      confirmOpen = false;
      page = 1;
      await load();
      confirmErr = `Видалено ${totalDeleted} контактів.`;
      setTimeout(() => (confirmErr = null), 4000);
    } catch (e) {
      confirmErr = e instanceof Error ? e.message : String(e);
    } finally {
      confirmBusy = false;
    }
  }

  // Draft filters (in the popover; copied to applied on Apply).
  let draftStatus = $state('');
  let draftTariff = $state('');
  let draftMinLtvUah = $state('');
  let draftMaxLtvUah = $state('');
  let draftMinPayments = $state('');
  let draftMaxPayments = $state('');
  let draftFirstSeenFrom = $state('');
  let draftFirstSeenTo = $state('');
  let draftLastSeenFrom = $state('');
  let draftLastSeenTo = $state('');
  let draftHasTelegram = $state<'' | 'true' | 'false'>('');
  let draftHasEmail = $state<'' | 'true' | 'false'>('');
  let draftHasPhone = $state<'' | 'true' | 'false'>('');

  // Filter builder — які поля користувач хоче бачити у panel'і.
  type FieldKey =
    | 'status' | 'tariff' | 'ltv' | 'payments'
    | 'firstSeen' | 'lastSeen'
    | 'hasTelegram' | 'hasEmail' | 'hasPhone';
  const ALL_FIELDS: { key: FieldKey; label: string }[] = [
    { key: 'status', label: 'Статус підписки' },
    { key: 'tariff', label: 'Тариф' },
    { key: 'ltv', label: 'LTV (₴)' },
    { key: 'payments', label: 'Кількість платежів' },
    { key: 'firstSeen', label: 'First seen' },
    { key: 'lastSeen', label: 'Last seen' },
    { key: 'hasTelegram', label: 'Має Telegram ID' },
    { key: 'hasEmail', label: 'Має email' },
    { key: 'hasPhone', label: 'Має телефон' },
  ];
  let enabledFields = $state<FieldKey[]>([]);
  let showAddField = $state(false);

  function fieldLabel(k: FieldKey): string {
    return ALL_FIELDS.find((f) => f.key === k)?.label ?? k;
  }
  function availableToAdd(): { key: FieldKey; label: string }[] {
    return ALL_FIELDS.filter((f) => !enabledFields.includes(f.key));
  }
  function addField(k: FieldKey) {
    if (!enabledFields.includes(k)) enabledFields = [...enabledFields, k];
    showAddField = false;
  }
  function removeField(k: FieldKey) {
    enabledFields = enabledFields.filter((x) => x !== k);
    // Зануляємо draft-значення видаленого поля.
    if (k === 'status') draftStatus = '';
    if (k === 'tariff') draftTariff = '';
    if (k === 'ltv') { draftMinLtvUah = ''; draftMaxLtvUah = ''; }
    if (k === 'payments') { draftMinPayments = ''; draftMaxPayments = ''; }
    if (k === 'firstSeen') { draftFirstSeenFrom = ''; draftFirstSeenTo = ''; }
    if (k === 'lastSeen') { draftLastSeenFrom = ''; draftLastSeenTo = ''; }
    if (k === 'hasTelegram') draftHasTelegram = '';
    if (k === 'hasEmail') draftHasEmail = '';
    if (k === 'hasPhone') draftHasPhone = '';
  }

  let showFilters = $state(false);
  let err = $state<string | null>(null);

  // Custom-поля з налаштувань ("Структура даних → users") — для filter-builder.
  let customFilterFields = $state<FieldDef[]>([]);
  /** Усі поля catalog (built-in + custom) для табличних колонок (schema-driven LIST). */
  let allFields = $state<ApiField[]>([]);
  /** Мета таблиці (редаговані назва/опис/колір/іконка з налаштувань).
   *  Беремо з кешованого стору → одразу без блимання при перемиканні. */
  const tableMeta = $derived($builtInTablesStore['users']);
  const tableName = $derived(tableMeta?.name ?? 'Користувачі');
  const tableDesc = $derived(tableMeta?.description ?? '');
  const tableColor = $derived(tableMeta?.color ?? '#7b4fbe');
  const tableIcon = $derived(tableMeta?.icon ?? 'users');

  /** Об'єднує built-in + custom fields у єдиний список для FilterBuilder. */
  const filterFields = $derived<FieldDef[]>([...fieldsForScope('persons'), ...customFilterFields]);

  const currentRole = $derived(($session?.role ?? null) as Role | null);

  /** In-memory override від view-пресету (null = використовує DB show_in_list). */
  let columnOverride = $state<string[] | null>(null);

  /** Видимі колонки у таблиці (built-in + custom), відсортовані за position.
   *  Built-in catalog default = -10000+i → першими. Admin може mixed-reorder
   *  через DnD у LIST header — після цього explicit positions переписують усі. */
  const tableColumns = $derived<ApiField[]>(
    columnOverride
      ? (columnOverride
          .map((key) => allFields.find((f) => f.fieldKey === key))
          .filter(Boolean)
          .filter((f) => canViewField(currentRole, f!.min_view_role)) as ApiField[])
      : allFields
          .filter(
            (f) =>
              !f.hidden &&
              f.show_in_list !== false &&
              canViewField(currentRole, f.min_view_role),
          )
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
  );

  let viewMode = $state<'list' | 'kanban'>(restored.viewMode ?? 'list');
  let kanbanFieldKey = $state<string | null>(restored.kanbanFieldKey ?? null);

  let granularity = $state<KanbanGranularity>(restored.granularity ?? 'month');
  let kanbanReload = $state(0);

  // telegramId — не колонка Person (з identities relation) → не групується серверно.
  const groupableFields = $derived(kanbanGroupableFields(allFields).filter((f) => f.fieldKey !== 'telegramId'));
  const kanbanField = $derived(groupableFields.find((f) => f.fieldKey === kanbanFieldKey) ?? null);
  const kanbanFieldType = $derived(kanbanField?.type ?? 'single_line');

  /** Чи можна переміщувати картки drag-and-drop (admin + settable поле). */
  const canDragKanban = $derived(isAdmin && !!kanbanField && fieldSupportsDrag(kanbanField) && canEditKanban(kanbanField));

  /** Поля доступні для налаштування картки. */
  const cardConfigFields = $derived(
    allFields.filter((f) => !f.hidden && f.type !== 'linked_summary' && f.type !== 'linked_record'),
  );

  /** Налаштування картки (з localStorage). */
  let cardConfig = $state<KanbanCardConfig>({ primaryFieldKey: null, fields: [], cardSize: 'medium' });
  let cardConfigOpen = $state(false);

  /** Зливаємо built-in та custom поля у плоский об'єкт для KanbanBoard. */
  function personToValues(r: PersonListItem): Record<string, unknown> {
    return {
      displayName: r.displayName,
      username: r.username,
      telegramId: r.telegramId,
      primaryEmail: r.primaryEmail,
      primaryPhone: r.primaryPhone,
      currentSubscriptionStatus: r.currentSubscriptionStatus,
      currentTariff: r.currentTariff,
      subscriptionEndsAt: r.subscriptionEndsAt,
      ltvCents: r.ltvCents,
      paymentCount: r.paymentCount,
      firstSeenAt: r.firstSeenAt,
      lastSeenAt: r.lastSeenAt,
      ...(r.customFields ?? {}),
    };
  }
  function personToRec(r: PersonListItem): ApiRecord {
    return {
      id: r.id,
      values: personToValues(r),
      createdAt: typeof r.firstSeenAt === 'string' ? r.firstSeenAt : String(r.firstSeenAt ?? ''),
      updatedAt: typeof r.lastSeenAt === 'string' ? r.lastSeenAt : String(r.lastSeenAt ?? ''),
    };
  }

  /** Поточні фільтри списку → query для kanban-ендпоінта. */
  function kanbanParams(): URLSearchParams {
    const p = new URLSearchParams();
    p.set('groupField', kanbanFieldKey ?? '');
    if (kanbanFieldType === 'date' || kanbanFieldType === 'datetime') p.set('granularity', granularity);
    if (status) p.set('status', status);
    if (tariff) p.set('tariff', tariff);
    if (search) p.set('search', search);
    if (minLtvCents != null) p.set('minLtvCents', String(minLtvCents));
    if (maxLtvCents != null) p.set('maxLtvCents', String(maxLtvCents));
    if (minPayments != null) p.set('minPayments', String(minPayments));
    if (maxPayments != null) p.set('maxPayments', String(maxPayments));
    if (firstSeenFrom) p.set('firstSeenFrom', firstSeenFrom);
    if (firstSeenTo) p.set('firstSeenTo', firstSeenTo);
    if (lastSeenFrom) p.set('lastSeenFrom', lastSeenFrom);
    if (lastSeenTo) p.set('lastSeenTo', lastSeenTo);
    if (hasTelegram != null) p.set('hasTelegram', String(hasTelegram));
    if (hasEmail != null) p.set('hasEmail', String(hasEmail));
    if (hasPhone != null) p.set('hasPhone', String(hasPhone));
    if (treeFilterCount > 0) p.set('filter', JSON.stringify(appliedTree));
    p.set('sort', sort);
    return p;
  }
  const kanbanSource: KanbanSource = {
    loadFacets: (cp) => fetchKanbanFacets('/persons/kanban', kanbanParams(), cp),
    loadCards: async (k, pg) => {
      const r = await fetchKanbanColumnRaw<PersonListItem>('/persons/kanban', kanbanParams(), k, pg);
      return { records: r.items.map(personToRec), total: r.total };
    },
    loadIds: async (k) => (await fetchKanbanColumnIds('/persons/kanban', kanbanParams(), k)).ids,
  };

  function bumpKanban() { kanbanReload += 1; }

  function onSwitchMode(m: 'list' | 'kanban') {
    viewMode = m;
    if (m === 'kanban') {
      cardConfig = loadCardConfig('users') ?? cardConfig;
      bumpKanban();
    }
  }

  function saveCard(c: KanbanCardConfig) {
    cardConfig = c;
    saveCardConfig('users', c);
    cardConfigOpen = false;
  }

  function applyViewPreset(v: ApiTableView) {
    if (v.columns.length > 0) columnOverride = v.columns;
    if (v.sortKey) sort = v.sortKey as SortValue;
    if (v.filters) appliedTree = v.filters as Group;
    page = 1;
    void load();
  }

  type BuiltInSortKey = 'ltv' | 'payments' | 'firstSeen' | 'lastSeen' | 'subscriptionEnds';

  /** Які built-in поля підтримують sort через listPersonsQuery.sort. */
  function sortKeyFor(fieldKey: string): BuiltInSortKey | null {
    if (fieldKey === 'ltvCents') return 'ltv';
    if (fieldKey === 'paymentCount') return 'payments';
    if (fieldKey === 'firstSeenAt') return 'firstSeen';
    if (fieldKey === 'lastSeenAt') return 'lastSeen';
    if (fieldKey === 'subscriptionEndsAt') return 'subscriptionEnds';
    return null;
  }

  /** Типи полів, за якими має сенс сортувати (числа, дати, булеви, агрегати). */
  const SORTABLE_FIELD_TYPES = new Set([
    'number', 'datetime', 'date', 'currency', 'auto_number', 'percent',
    'checkbox', 'formula', 'linked_summary', 'stat',
  ]);
  function isSortableType(type: string): boolean {
    return SORTABLE_FIELD_TYPES.has(type);
  }

  /** Inline cell-edit для editable cells у LIST (Airtable-style).
   *  Підтримує custom-поля + прості built-in (currentTariff, currentSubscriptionStatus). */
  /** Built-in поля які можна редагувати inline (мають column на Person, не computed). */
  const EDITABLE_BUILTIN = new Set<string>([
    'currentTariff',
    'currentSubscriptionStatus',
    'subscriptionEndsAt',
    'username',
  ]);

  /** Чи можна редагувати клітинку поля у LIST (popover-редактор). */
  function isPersonCellEditable(col: ApiField): boolean {
    if (!isAdmin) return false;
    if (col.is_computed || col.type === 'formula' || col.type === 'stat' || col.type === 'linked_summary') {
      return false;
    }
    return col.builtIn ? EDITABLE_BUILTIN.has(col.fieldKey) : true;
  }

  /** Зберегти одне поле запису (popover-редактор клітинки). Кидає помилку — CellEdit покаже alert. */
  async function persistPersonCell(personId: string, col: ApiField, value: unknown) {
    const v = value === '' || value === undefined ? null : value;
    const body = col.builtIn
      ? ({ [col.fieldKey]: v } as Record<string, unknown>)
      : { customFields: { [col.fieldKey]: v } };
    await updatePerson(personId, body);
    const idx = items.findIndex((it) => it.id === personId);
    if (idx >= 0) {
      const next = { ...items[idx]! };
      if (col.builtIn) {
        (next as unknown as Record<string, unknown>)[col.fieldKey] = v;
      } else {
        const cf = { ...(next.customFields ?? {}) };
        if (v === null) delete cf[col.fieldKey];
        else cf[col.fieldKey] = v;
        next.customFields = cf;
      }
      items[idx] = next;
      items = [...items];
    }
  }

  /** Поля доступні для bulk-edit: built-in editable + custom не-computed/не-linked. */
  const bulkEditableFields = $derived<ApiField[]>(
    allFields.filter(
      (f) =>
        !f.is_computed &&
        !f.hidden &&
        f.type !== 'linked_record' &&
        f.type !== 'linked_summary' &&
        (f.builtIn ? EDITABLE_BUILTIN.has(f.fieldKey) : true),
    ),
  );

  /** Per-id apply: built-in → top-level body, custom → customFields jsonb merge. */
  async function applyBulkEditToOne(id: string, field: ApiField, value: unknown): Promise<void> {
    const body = field.builtIn
      ? ({ [field.fieldKey]: value } as Record<string, unknown>)
      : { customFields: { [field.fieldKey]: value } };
    await updatePerson(id, body);
  }

  /** Built-in поля, що логічно НЕ редагуються (identity / computed). */
  const KANBAN_LOCKED_BUILTIN = new Set<string>(['telegramId']);

  /** Чи можна редагувати поле на kanban-картці. Усі крім логічно обмежених. */
  function canEditKanban(f: ApiField): boolean {
    if (f.is_computed) return false;
    return f.builtIn ? !KANBAN_LOCKED_BUILTIN.has(f.fieldKey) : true;
  }

  /** Інлайн-редагування поля на kanban-картці (board оновлює локально). */
  async function onKanbanCellEdit(recordId: string, field: ApiField, value: unknown) {
    const body = field.builtIn
      ? ({ [field.fieldKey]: value } as Record<string, unknown>)
      : { customFields: { [field.fieldKey]: value } };
    await updatePerson(recordId, body);
  }

  /** Переміщення картки між колонками — встановлює значення group-поля. */
  async function onKanbanCardMove(recordId: string, newValue: unknown) {
    if (!kanbanFieldKey) return;
    const field = allFields.find((f) => f.fieldKey === kanbanFieldKey);
    if (!field) return;
    const body = field.builtIn
      ? ({ [field.fieldKey]: newValue } as Record<string, unknown>)
      : { customFields: { [field.fieldKey]: newValue } };
    await updatePerson(recordId, body);
  }

  /** Realtime: debounced bump дошки при record-changed з SSE. */
  let kanbanRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  function scheduleKanbanRefresh() {
    if (viewMode !== 'kanban') return;
    if (kanbanRefreshTimer) clearTimeout(kanbanRefreshTimer);
    kanbanRefreshTimer = setTimeout(() => { kanbanRefreshTimer = null; bumpKanban(); }, 600);
  }

  const exportParams = $derived<Record<string, unknown>>({
    columns: tableColumns.map((f) => f.fieldKey),
    sort,
    filter: treeFilterCount > 0 ? JSON.stringify(appliedTree) : undefined,
    status: status || undefined,
    tariff: tariff || undefined,
    search: search || undefined,
    minLtvCents: minLtvCents ?? undefined,
    maxLtvCents: maxLtvCents ?? undefined,
    minPayments: minPayments ?? undefined,
    maxPayments: maxPayments ?? undefined,
    firstSeenFrom: firstSeenFrom || undefined,
    firstSeenTo: firstSeenTo || undefined,
    lastSeenFrom: lastSeenFrom || undefined,
    lastSeenTo: lastSeenTo || undefined,
    hasTelegram: hasTelegram ?? undefined,
    hasEmail: hasEmail ?? undefined,
    hasPhone: hasPhone ?? undefined,
  });

  async function onBulkEditDone(progress: { done: number; total: number; errors: number }) {
    confirmErr = `Оновлено ${progress.done - progress.errors} з ${progress.total}${progress.errors > 0 ? ` (помилки: ${progress.errors})` : ''}`;
    setTimeout(() => (confirmErr = null), 6000);
    bulkEditOpen = false;
    clearSelection();
    await load();
  }

  // Tree-based filter (new builder). Applied state — те що шле сервер.
  let appliedTree = $state<Group>(restored.appliedTree);
  // Draft tree — те що користувач редагує у відкритому модалі.
  let draftTree = $state<Group>(emptyGroup());
  let showBuilder = $state(false);
  let savedFilters = $state<SavedFilter[]>([]);

  async function loadSavedFilters() {
    try {
      savedFilters = await fetchSavedFilters('persons');
    } catch {
      savedFilters = [];
    }
  }

  const treeFilterCount = $derived(countActive(appliedTree));

  // Будь-яка зміна фільтрів/сорту/пагінації — пишемо у sessionStorage.
  // Це дозволить повернутися назад зі сторінки PersonDetail / іншої з тим
  // самим стейтом.
  $effect(() => {
    saveState<PersistedState>('persons-list', {
      search,
      status,
      tariff,
      minLtvCents,
      maxLtvCents,
      minPayments,
      maxPayments,
      firstSeenFrom,
      firstSeenTo,
      lastSeenFrom,
      lastSeenTo,
      hasTelegram,
      hasEmail,
      hasPhone,
      sort,
      page,
      pageSize,
      appliedTree,
      viewMode,
      kanbanFieldKey,
      granularity,
    });
  });

  function openBuilder() {
    // Deep-clone, щоб Cancel не зачіпав applied.
    draftTree = JSON.parse(JSON.stringify(appliedTree)) as Group;
    showBuilder = true;
    void loadSavedFilters();
  }
  function applyBuilder() {
    appliedTree = draftTree;
    showBuilder = false;
    page = 1;
    void load();
  }
  function cancelBuilder() {
    showBuilder = false;
  }
  function clearBuilder() {
    draftTree = emptyGroup();
  }

  const activeFilterCount = $derived(
    (status ? 1 : 0) +
      (tariff ? 1 : 0) +
      (minLtvCents !== null ? 1 : 0) +
      (maxLtvCents !== null ? 1 : 0) +
      (minPayments !== null ? 1 : 0) +
      (maxPayments !== null ? 1 : 0) +
      (firstSeenFrom ? 1 : 0) +
      (firstSeenTo ? 1 : 0) +
      (lastSeenFrom ? 1 : 0) +
      (lastSeenTo ? 1 : 0) +
      (hasTelegram !== null ? 1 : 0) +
      (hasEmail !== null ? 1 : 0) +
      (hasPhone !== null ? 1 : 0),
  );

  async function load() {
    loading = true;
    err = null;
    try {
      const r = await fetchPersons({
        status: status || undefined,
        tariff: tariff || undefined,
        search: search || undefined,
        minLtvCents: minLtvCents ?? undefined,
        maxLtvCents: maxLtvCents ?? undefined,
        minPayments: minPayments ?? undefined,
        maxPayments: maxPayments ?? undefined,
        firstSeenFrom: firstSeenFrom || undefined,
        firstSeenTo: firstSeenTo || undefined,
        lastSeenFrom: lastSeenFrom || undefined,
        lastSeenTo: lastSeenTo || undefined,
        hasTelegram: hasTelegram ?? undefined,
        hasEmail: hasEmail ?? undefined,
        hasPhone: hasPhone ?? undefined,
        filter: treeFilterCount > 0 ? JSON.stringify(appliedTree) : undefined,
        sort,
        page,
        pageSize,
      });
      items = r.items;
      total = r.total;
      // У kanban-режимі список фільтрів/сорту той самий — перевантажуємо дошку.
      if (viewMode === 'kanban') bumpKanban();
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
    }
  }
  async function reloadSchema() {
    try {
      const schema = await fetchPublicDataTable('users');
      allFields = schema.fields ?? [];
      const customFields = allFields.filter((f) => !f.builtIn);
      customFilterFields = customFields.map(apiFieldToFilterDef);
    } catch {
      allFields = [];
      customFilterFields = [];
    }
  }

  let unsubSchemaSSE: (() => void) | null = null;

  onMount(async () => {
    if (viewMode === 'kanban') cardConfig = loadCardConfig('users') ?? cardConfig;
    await reloadSchema();
    await load(); // у kanban-режимі load() сам перевантажить дошку
    unsubSchemaSSE = subscribeSchemaSSE(
      'users',
      () => void reloadSchema(),
      () => scheduleKanbanRefresh(),
    );
  });

  onDestroy(() => unsubSchemaSSE?.());

  // Live-search debounce — 300 мс після останнього keystroke.
  let searchTimer: ReturnType<typeof setTimeout> | null = null;
  function onSearchInput() {
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      page = 1;
      void load();
    }, 300);
  }

  /**
   * Перемикач сортування по колонці. Якщо клікнули по поточній — інвертуємо
   * напрямок. Якщо по іншій — стартуємо з Desc (найбільш очікувано: останнє
   * / найбільше — згори).
   */
  function toggleSort(column: BuiltInSortKey) {
    if (suppressSortClick) return;
    const desc = `${column}Desc` as SortValue;
    const asc = `${column}Asc` as SortValue;
    sort = sort === desc ? asc : sort === asc ? desc : desc;
    page = 1;
    void load();
  }
  function sortIcon(column: BuiltInSortKey): string {
    if (sort === `${column}Desc`) return '▼';
    if (sort === `${column}Asc`) return '▲';
    return '↕';
  }
  /** Sort за custom fieldKey (JSONB path). */
  function toggleSortCustom(fieldKey: string) {
    if (suppressSortClick) return;
    const desc = `custom:${fieldKey}:desc` as SortValue;
    const asc = `custom:${fieldKey}:asc` as SortValue;
    sort = sort === desc ? asc : sort === asc ? desc : desc;
    page = 1;
    void load();
  }
  function sortIconCustom(fieldKey: string): string {
    if (sort === `custom:${fieldKey}:desc`) return '▼';
    if (sort === `custom:${fieldKey}:asc`) return '▲';
    return '↕';
  }

  /** Drag&drop reorder колонок у LIST header (HTML5 native). */
  let draggedColId = $state<string | null>(null);
  let dropTargetColId = $state<string | null>(null);

  // ── Resize колонок (table-fixed, ширина зберігається у localStorage) ──
  const COL_MIN = 80, COL_MAX = 600;
  let colWidths = $state<Record<string, number>>(loadColumnWidths('users', '__list__'));
  /** Дефолтна ширина колонки за типом (якщо користувач ще не змінював). */
  function defColW(col: ApiField): number {
    if (colWidths[col.fieldKey]) return colWidths[col.fieldKey];
    const t = col.type, k = col.fieldKey;
    if (k === 'displayName') return 260;
    if (t === 'email') return 220;
    if (t === 'multi_line') return 240;
    if (t === 'datetime' || t === 'date') return 140;
    if (t === 'number' || t === 'currency' || t === 'percent' || t === 'auto_number' || t === 'stat') return 120;
    if (t === 'checkbox') return 90;
    return 170;
  }
  function colStyle(key: string): string {
    const col = tableColumns.find((c) => c.fieldKey === key);
    return `width:${col ? defColW(col) : 170}px`;
  }
  const totalColW = $derived((isAdmin ? 44 : 0) + tableColumns.reduce((s, c) => s + defColW(c), 0));
  let colResizing: { key: string; startX: number; startW: number } | null = null;
  /** Блокує sort-клік на заголовку одразу після ресайзу (інакше click сортує). */
  let suppressSortClick = false;
  function startColResize(e: PointerEvent, key: string) {
    e.preventDefault();
    e.stopPropagation();
    suppressSortClick = true;
    const th = (e.currentTarget as HTMLElement).parentElement as HTMLElement;
    const startW = colWidths[key] ?? th.getBoundingClientRect().width;
    colResizing = { key, startX: e.clientX, startW };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }
  function onColResizeMove(e: PointerEvent) {
    if (!colResizing) return;
    const w = Math.max(COL_MIN, Math.min(COL_MAX, colResizing.startW + (e.clientX - colResizing.startX)));
    colWidths = { ...colWidths, [colResizing.key]: w };
  }
  function endColResize() {
    if (!colResizing) return;
    saveColumnWidths('users', '__list__', colWidths);
    colResizing = null;
    setTimeout(() => { suppressSortClick = false; }, 250);
  }

  function onColDragStart(e: DragEvent, fieldId: string) {
    if (!e.dataTransfer) return;
    draggedColId = fieldId;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', fieldId);
  }
  function onColDragOver(e: DragEvent, fieldId: string) {
    if (!draggedColId || draggedColId === fieldId) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    dropTargetColId = fieldId;
  }
  function onColDragLeave() {
    dropTargetColId = null;
  }
  async function onColDrop(e: DragEvent, targetFieldId: string) {
    e.preventDefault();
    const sourceId = draggedColId;
    draggedColId = null;
    dropTargetColId = null;
    if (!sourceId || sourceId === targetFieldId) return;
    const cols = [...tableColumns];
    const fromIdx = cols.findIndex((c) => c.id === sourceId);
    const toIdx = cols.findIndex((c) => c.id === targetFieldId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = cols.splice(fromIdx, 1);
    cols.splice(toIdx, 0, moved!);
    try {
      await reorderDataFields(
        'users',
        cols.map((c, i) => ({ id: c.id, position: i, groupName: c.groupName ?? null })),
      );
      const schema = await fetchPublicDataTable('users');
      allFields = schema.fields ?? [];
    } catch (e2) {
      err = e2 instanceof Error ? e2.message : String(e2);
    }
  }

  function onPageChange(p: number) {
    page = p;
    void load();
  }
  function onPageSizeChange(s: number) {
    pageSize = s;
    page = 1;
    void load();
  }

  function uahToCents(uah: string): number | null {
    const n = Number(uah);
    return uah && Number.isFinite(n) ? Math.round(n * 100) : null;
  }
  function intOrNull(s: string): number | null {
    const n = Number(s);
    return s && Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
  }
  function centsToUah(cents: number | null): string {
    return cents != null ? String(Math.round(cents / 100)) : '';
  }

  function triToStr(v: boolean | null): '' | 'true' | 'false' {
    return v === true ? 'true' : v === false ? 'false' : '';
  }
  function strToTri(s: '' | 'true' | 'false'): boolean | null {
    return s === 'true' ? true : s === 'false' ? false : null;
  }
  function openFilters() {
    draftStatus = status;
    draftTariff = tariff;
    draftMinLtvUah = centsToUah(minLtvCents);
    draftMaxLtvUah = centsToUah(maxLtvCents);
    draftMinPayments = minPayments != null ? String(minPayments) : '';
    draftMaxPayments = maxPayments != null ? String(maxPayments) : '';
    draftFirstSeenFrom = firstSeenFrom;
    draftFirstSeenTo = firstSeenTo;
    draftLastSeenFrom = lastSeenFrom;
    draftLastSeenTo = lastSeenTo;
    draftHasTelegram = triToStr(hasTelegram);
    draftHasEmail = triToStr(hasEmail);
    draftHasPhone = triToStr(hasPhone);

    // Авто-показати поля які зараз АКТИВНІ — щоб користувач не загубив їх.
    const active: FieldKey[] = [];
    if (status) active.push('status');
    if (tariff) active.push('tariff');
    if (minLtvCents !== null || maxLtvCents !== null) active.push('ltv');
    if (minPayments !== null || maxPayments !== null) active.push('payments');
    if (firstSeenFrom || firstSeenTo) active.push('firstSeen');
    if (lastSeenFrom || lastSeenTo) active.push('lastSeen');
    if (hasTelegram !== null) active.push('hasTelegram');
    if (hasEmail !== null) active.push('hasEmail');
    if (hasPhone !== null) active.push('hasPhone');
    enabledFields = active;

    showFilters = true;
    showAddField = false;
  }
  function applyFilters() {
    // Тільки enabled поля переходять у applied state — решта обнуляється.
    status = enabledFields.includes('status') ? draftStatus : '';
    tariff = enabledFields.includes('tariff') ? draftTariff : '';
    minLtvCents = enabledFields.includes('ltv') ? uahToCents(draftMinLtvUah) : null;
    maxLtvCents = enabledFields.includes('ltv') ? uahToCents(draftMaxLtvUah) : null;
    minPayments = enabledFields.includes('payments') ? intOrNull(draftMinPayments) : null;
    maxPayments = enabledFields.includes('payments') ? intOrNull(draftMaxPayments) : null;
    firstSeenFrom = enabledFields.includes('firstSeen') ? draftFirstSeenFrom : '';
    firstSeenTo = enabledFields.includes('firstSeen') ? draftFirstSeenTo : '';
    lastSeenFrom = enabledFields.includes('lastSeen') ? draftLastSeenFrom : '';
    lastSeenTo = enabledFields.includes('lastSeen') ? draftLastSeenTo : '';
    hasTelegram = enabledFields.includes('hasTelegram') ? strToTri(draftHasTelegram) : null;
    hasEmail = enabledFields.includes('hasEmail') ? strToTri(draftHasEmail) : null;
    hasPhone = enabledFields.includes('hasPhone') ? strToTri(draftHasPhone) : null;
    showFilters = false;
    page = 1;
    void load();
  }
  function clearAllFilters() {
    status = '';
    tariff = '';
    minLtvCents = null;
    maxLtvCents = null;
    minPayments = null;
    maxPayments = null;
    firstSeenFrom = '';
    firstSeenTo = '';
    lastSeenFrom = '';
    lastSeenTo = '';
    hasTelegram = null;
    hasEmail = null;
    hasPhone = null;
    enabledFields = [];
    showFilters = false;
    page = 1;
    void load();
  }
</script>

<svelte:window onpointermove={onColResizeMove} onpointerup={endColResize} />

<AppShell active="persons">
  <div class="mx-auto max-w-[1200px]">
    <header class="mb-5 flex items-end justify-between gap-3 flex-wrap">
      <div class="flex items-center gap-3">
        <div class="grid h-11 w-11 shrink-0 place-items-center rounded-full text-white" style="background: {tableColor}">
          <Icon name={tableIconName(tableIcon)} size={20} />
        </div>
        <div>
          <h1 class="text-xl font-semibold leading-tight">
            {tableName} <span class="text-sm text-[var(--color-text-muted)] font-medium ml-1">· {fmtInt(total)}</span>
          </h1>
          <p class="mt-0.5 text-[12px] text-[var(--color-text-muted)] min-h-[16px]">{tableDesc}</p>
        </div>
      </div>
      {#if isAdmin}
        <div class="flex items-center gap-2">
          <a
            href="#/settings/data-structure/tables/users"
            class="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border-3)] bg-white px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] hover:bg-[var(--color-bg-3)]"
          >
            <Icon name="settings" size={14} /> Налаштування
          </a>
          <div class="relative inline-flex">
            <button
              onclick={() => { actionsMenuOpen = false; resetCreateForm(); createOpen = true; }}
              class="rounded-l-md btn-pu px-3 py-1.5 text-xs font-semibold"
            >
              + Новий запис
            </button>
            <button
              onclick={() => (actionsMenuOpen = !actionsMenuOpen)}
              aria-label="Більше дій"
              class="inline-flex items-center rounded-r-md btn-pu border-l border-white/30 px-2 py-1.5 text-xs"
            >
              <Icon name="chevronDown" size={15} />
            </button>
            {#if actionsMenuOpen}
              <button
                type="button"
                onclick={() => (actionsMenuOpen = false)}
                class="fixed inset-0 z-20 cursor-default"
                aria-label="Close menu"
              ></button>
              <div class="absolute right-0 top-full mt-1 z-30 min-w-[200px] rounded-md border border-[var(--color-border-3)] bg-white shadow-lg overflow-hidden">
                <button
                  type="button"
                  onclick={() => { actionsMenuOpen = false; resetCreateForm(); createOpen = true; }}
                  class="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-[var(--color-bg-3)]"
                >
                  <Icon name="add" size={14} /><span>Новий запис</span>
                </button>
                <a
                  href="#/settings/data-structure/import/users"
                  onclick={() => (actionsMenuOpen = false)}
                  class="flex items-center gap-2 px-3 py-2 text-xs hover:bg-[var(--color-bg-3)] no-underline text-[var(--color-text)]"
                >
                  <Icon name="download" size={14} /><span>Імпорт з файлу</span>
                </a>
              </div>
            {/if}
          </div>
        </div>
      {/if}
    </header>

    <!-- Toolbar: два рядки -->
    <div class="relative mb-4 space-y-2">
      <!-- Рядок 1: Пошук + Фільтр + CSV -->
      <div class="flex flex-wrap items-center gap-2">
        <div class="w-full sm:flex-1 relative">
          <span class="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted-2)] flex"><Icon name="search" size={14} /></span>
          <input
            bind:value={search}
            oninput={onSearchInput}
            placeholder="Пошук у Користувачі"
            class="w-full rounded-md border border-[var(--color-border-3)] bg-white pl-8 pr-2.5 py-1.5 text-xs"
          />
        </div>
        <button
          onclick={openBuilder}
          class="relative inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors
            {treeFilterCount > 0
              ? 'border-[var(--color-pu)] bg-purple-50 text-[var(--color-pu-3)]'
              : 'border-[var(--color-border-3)] bg-white text-[var(--color-text)] hover:bg-[var(--color-bg-3)]'}"
        >
          <Icon name="filter" size={14} /> Фільтр
          {#if treeFilterCount > 0}
            <span class="ml-1 inline-block rounded-full bg-[var(--color-pu)] text-white text-[9.5px] font-bold px-1.5 py-0.5 leading-none">
              {treeFilterCount}
            </span>
          {/if}
        </button>
        {#if treeFilterCount > 0}
          <button onclick={() => { appliedTree = emptyGroup(); page = 1; void load(); }} class="text-[10.5px] text-[var(--color-text-muted)] hover:underline">
            скинути всі
          </button>
        {/if}
        <ExportHistoryButton
          tableSlug="users"
          label="Користувачі · {new Date().toLocaleDateString('uk-UA')}"
          totalCount={total}
          params={exportParams}
        />
      </div>

      <!-- Рядок 2: Вид + Колонки + Порядок + Views -->
      <div class="flex flex-wrap items-center gap-2">
        <ViewModeToggle
          mode={viewMode}
          {kanbanFieldKey}
          {groupableFields}
          {granularity}
          onModeChange={onSwitchMode}
          onKanbanFieldChange={(k) => { kanbanFieldKey = k; if (viewMode === 'kanban') bumpKanban(); }}
          onGranularityChange={(g) => { granularity = g; if (viewMode === 'kanban') bumpKanban(); }}
        />
        <ColumnVisibilityMenu
          slug="users"
          fields={allFields.filter((f) => canViewField(currentRole, f.min_view_role))}
          onChanged={async () => {
            try {
              const schema = await fetchPublicDataTable('users');
              allFields = schema.fields ?? [];
            } catch {}
          }}
        />
        <ViewPresetsMenu
          slug="users"
          currentColumns={tableColumns.map((f) => f.fieldKey)}
          currentSortKey={sort}
          currentFilters={treeFilterCount > 0 ? appliedTree : null}
          onApply={applyViewPreset}
          {isAdmin}
        />
      </div>

      {#if showFilters}
        <div class="absolute right-0 top-full mt-2 z-20 w-[380px] rounded-xl border border-[var(--color-border-3)] bg-white p-4 shadow-xl">
          <div class="text-[11px] font-bold uppercase tracking-[1px] text-[var(--color-text-muted-2)] mb-3">
            Фільтр користувачів
          </div>

          {#if enabledFields.length === 0}
            <div class="rounded-md border border-dashed border-[var(--color-border-3)] bg-[var(--color-bg-3)] px-3 py-4 text-center text-[11px] text-[var(--color-text-muted)] mb-3">
              Поки немає полів. Натисніть «+ Поле» нижче, щоб додати фільтр.
            </div>
          {:else}
            <div class="space-y-3">
              {#each enabledFields as fk (fk)}
                <div class="rounded-md border border-[var(--color-border-3)] bg-[var(--color-bg-3)]/40 p-2.5">
                  <div class="flex items-center justify-between mb-1.5">
                    <div class="text-[10.5px] font-semibold">{fieldLabel(fk)}</div>
                    <button onclick={() => removeField(fk)} title="Прибрати поле" class="inline-flex items-center text-[var(--color-text-muted)] hover:text-red-600"><Icon name="close" size={13} /></button>
                  </div>
                  {#if fk === 'status'}
                    <select bind:value={draftStatus} class="w-full rounded-md border border-[var(--color-border-3)] bg-white px-2.5 py-1 text-xs">
                      <option value="">Усі статуси</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="none">No subscription</option>
                    </select>
                  {:else if fk === 'tariff'}
                    <input bind:value={draftTariff} placeholder="contains, напр. клуб"
                      class="w-full rounded-md border border-[var(--color-border-3)] bg-white px-2.5 py-1.5 text-xs" />
                  {:else if fk === 'ltv'}
                    <div class="grid grid-cols-2 gap-2">
                      <input bind:value={draftMinLtvUah} type="number" min="0" placeholder="від, ₴"
                        class="rounded-md border border-[var(--color-border-3)] bg-white px-2.5 py-1 text-xs" />
                      <input bind:value={draftMaxLtvUah} type="number" min="0" placeholder="до, ₴"
                        class="rounded-md border border-[var(--color-border-3)] bg-white px-2.5 py-1 text-xs" />
                    </div>
                  {:else if fk === 'payments'}
                    <div class="grid grid-cols-2 gap-2">
                      <input bind:value={draftMinPayments} type="number" min="0" placeholder="від"
                        class="rounded-md border border-[var(--color-border-3)] bg-white px-2.5 py-1 text-xs" />
                      <input bind:value={draftMaxPayments} type="number" min="0" placeholder="до"
                        class="rounded-md border border-[var(--color-border-3)] bg-white px-2.5 py-1 text-xs" />
                    </div>
                  {:else if fk === 'firstSeen'}
                    <div class="grid grid-cols-2 gap-2">
                      <input bind:value={draftFirstSeenFrom} type="date"
                        class="rounded-md border border-[var(--color-border-3)] bg-white px-2.5 py-1 text-xs" />
                      <input bind:value={draftFirstSeenTo} type="date"
                        class="rounded-md border border-[var(--color-border-3)] bg-white px-2.5 py-1 text-xs" />
                    </div>
                  {:else if fk === 'lastSeen'}
                    <div class="grid grid-cols-2 gap-2">
                      <input bind:value={draftLastSeenFrom} type="date"
                        class="rounded-md border border-[var(--color-border-3)] bg-white px-2.5 py-1 text-xs" />
                      <input bind:value={draftLastSeenTo} type="date"
                        class="rounded-md border border-[var(--color-border-3)] bg-white px-2.5 py-1 text-xs" />
                    </div>
                  {:else if fk === 'hasTelegram'}
                    <select bind:value={draftHasTelegram} class="w-full rounded-md border border-[var(--color-border-3)] bg-white px-2.5 py-1 text-xs">
                      <option value="">— байдуже —</option>
                      <option value="true">Має Telegram ID</option>
                      <option value="false">НЕ має Telegram ID</option>
                    </select>
                  {:else if fk === 'hasEmail'}
                    <select bind:value={draftHasEmail} class="w-full rounded-md border border-[var(--color-border-3)] bg-white px-2.5 py-1 text-xs">
                      <option value="">— байдуже —</option>
                      <option value="true">Має email</option>
                      <option value="false">НЕ має email</option>
                    </select>
                  {:else if fk === 'hasPhone'}
                    <select bind:value={draftHasPhone} class="w-full rounded-md border border-[var(--color-border-3)] bg-white px-2.5 py-1 text-xs">
                      <option value="">— байдуже —</option>
                      <option value="true">Має телефон</option>
                      <option value="false">НЕ має телефону</option>
                    </select>
                  {/if}
                </div>
              {/each}
            </div>
          {/if}

          <!-- Add field menu -->
          <div class="relative mt-3">
            {#if availableToAdd().length > 0}
              <button onclick={() => (showAddField = !showAddField)}
                class="w-full rounded-md border border-dashed border-[var(--color-border-3)] bg-white px-3 py-1.5 text-[11px] font-semibold text-[var(--color-pu-3)] hover:bg-[var(--color-bg-3)]">
                + Поле
              </button>
              {#if showAddField}
                <div class="absolute left-0 right-0 top-full mt-1 z-10 max-h-[200px] overflow-y-auto rounded-md border border-[var(--color-border-3)] bg-white shadow-lg">
                  {#each availableToAdd() as opt (opt.key)}
                    <button onclick={() => addField(opt.key)}
                      class="block w-full text-left px-3 py-1.5 text-[11px] hover:bg-[var(--color-bg-3)]">
                      {opt.label}
                    </button>
                  {/each}
                </div>
              {/if}
            {:else}
              <div class="text-[10.5px] text-[var(--color-text-muted)] text-center py-1">
                Усі поля вже додані.
              </div>
            {/if}
          </div>

          <div class="mt-4 flex items-center justify-between border-t border-[var(--color-border-3)] pt-3">
            <button onclick={() => { enabledFields = []; }} class="text-[11px] text-[var(--color-text-muted)] hover:underline">
              Прибрати всі
            </button>
            <div class="flex gap-2">
              <button onclick={() => (showFilters = false)} class="rounded-md border border-[var(--color-border-3)] px-3 py-1.5 text-[11px] font-medium">
                Скасувати
              </button>
              <button onclick={applyFilters} class="rounded-md bg-[var(--color-pu)] px-3 py-1.5 text-[11px] font-semibold text-white">
                Застосувати
              </button>
            </div>
          </div>
        </div>
      {/if}
    </div>

    {#if loading}
      <div class="py-20 text-center text-sm text-[var(--color-text-muted)]">Завантаження…</div>
    {:else if err}
      <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{err}</div>
    {:else}
      <!-- Bulk action bar — список (у kanban — плаваюча KanbanBulkBar) -->
      {#if viewMode === 'list' && isAdmin && selected.size > 0}
        <div class="mb-2 flex items-center gap-3 rounded-lg border border-[var(--color-pu)]/40 bg-purple-50 px-3.5 py-2 text-xs">
          <span class="font-semibold">Вибрано: {selected.size}</span>
          {#if selected.size < total}
            <button onclick={selectAllUnderFilter} class="text-[var(--color-pu-3)] hover:underline">
              Вибрати все ({fmtInt(total)})
            </button>
          {/if}
          <button onclick={clearSelection} class="text-[var(--color-text-muted)] hover:underline ml-auto">
            скинути
          </button>
          <button
            onclick={() => (bulkEditOpen = true)}
            class="inline-flex items-center gap-1.5 rounded-md btn-pu px-3 py-1.5 text-[11px] font-semibold"
          >
            <Icon name="edit" size={13} /> Редагувати
          </button>
          <button
            onclick={() => { confirmOpen = true; confirmErr = null; }}
            class="inline-flex items-center gap-1.5 rounded-md bg-red-600 text-white px-3 py-1.5 text-[11px] font-semibold hover:bg-red-700"
          >
            <Icon name="delete" size={13} /> Видалити
          </button>
        </div>
      {/if}
      {#if confirmErr && !confirmOpen}
        <div class="mb-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[11px] text-emerald-700">
          {confirmErr}
        </div>
      {/if}

      {#if viewMode === 'kanban' && kanbanFieldKey}
        <KanbanBoard
          slug="users"
          groupFieldKey={kanbanFieldKey}
          groupFieldType={kanbanFieldType}
          source={kanbanSource}
          reloadToken={kanbanReload}
          canDragGroup={canDragKanban}
          fields={cardConfigFields}
          config={cardConfig}
          showAvatar={true}
          onConfigClick={() => (cardConfigOpen = true)}
          onRecordClick={(id) => push(`/persons/${id}`)}
          onCellEdit={isAdmin ? onKanbanCellEdit : undefined}
          onCardMove={canDragKanban ? onKanbanCardMove : undefined}
          canEdit={canEditKanban}
          selectable={isAdmin}
          selectedIds={selected}
          onSelectionChange={(s) => (selected = s)}
        />
        <KanbanBulkBar
          count={selected.size}
          onEdit={() => (bulkEditOpen = true)}
          onDelete={() => { confirmOpen = true; confirmErr = null; }}
          onClear={clearSelection}
        />
      {:else}

      <div class="overflow-hidden rounded-2xl border border-[var(--color-border-2)] bg-[var(--color-surface)] shadow-card">
        <div class="overflow-x-auto">
        <table class="table-fixed text-[13px] [&_td]:overflow-hidden [&_th]:overflow-hidden" style="width: {totalColW}px;">
          <thead class="sticky top-0 z-[1] glass">
            <tr class="text-[10.5px] uppercase tracking-[0.08em] text-[var(--color-text-muted-2)] border-b border-[var(--color-border-2)]">
              {#if isAdmin}
                <th class="text-left px-3 py-2 w-[30px]">
                  <input
                    type="checkbox"
                    checked={allOnPageSelected}
                    onclick={(e) => { e.stopPropagation(); togglePage(); }}
                    title="Вибрати усі на цій сторінці"
                  />
                </th>
              {/if}
              <!-- Schema-driven headers (built-in + custom) — порядок з catalog position.
                   Admin reorder/hide через Settings → Структура даних. -->
              {#each tableColumns as col (col.id)}
                {@const sortK = sortKeyFor(col.fieldKey)}
                {@const isDropTarget = dropTargetColId === col.id}
                {#if sortK}
                  <th
                    style={colStyle(col.fieldKey)} class="relative group/col text-left px-3 py-2 font-bold cursor-pointer select-none hover:bg-[var(--color-bg-2)] {isDropTarget ? 'bg-[var(--color-pu)]/10 outline-2 outline-dashed outline-[var(--color-pu)]' : ''}"
                    onclick={() => toggleSort(sortK)}
                    ondragover={(e) => onColDragOver(e, col.id)}
                    ondragleave={onColDragLeave}
                    ondrop={(e) => onColDrop(e, col.id)}
                    title="Клік — sort · drag — перемістити колонку"
                  >
                    {#if isAdmin}<span draggable="true" ondragstart={(e) => onColDragStart(e, col.id)} onclick={(e) => e.stopPropagation()} class="absolute left-1.5 top-1/2 -translate-y-1/2 inline-flex text-[var(--color-text-muted-2)] cursor-grab opacity-0 group-hover/col:opacity-100 transition-opacity z-[1]" title="Перетягнути колонку"><Icon name="grip" size={13} /></span>{/if}
<span class="block truncate align-middle transition-transform duration-150 group-hover/col:translate-x-[15px]">{col.label} <span class="text-[var(--color-pu-3)]">{sortIcon(sortK)}</span></span>
                    <div class="col-resize" role="separator" aria-orientation="vertical" onpointerdown={(e) => startColResize(e, col.fieldKey)}></div>
                  </th>
                {:else if !col.builtIn && isSortableType(col.type)}
                  <!-- Кастомні поля числового/датового типу — сортуються -->
                  <th
                    style={colStyle(col.fieldKey)} class="relative group/col text-left px-3 py-2 font-bold cursor-pointer select-none hover:bg-[var(--color-bg-2)] {isDropTarget ? 'bg-[var(--color-pu)]/10 outline-2 outline-dashed outline-[var(--color-pu)]' : ''}"
                    onclick={() => toggleSortCustom(col.fieldKey)}
                    ondragover={(e) => onColDragOver(e, col.id)}
                    ondragleave={onColDragLeave}
                    ondrop={(e) => onColDrop(e, col.id)}
                    title="Клік — sort · drag — перемістити колонку"
                  >
                    {#if isAdmin}<span draggable="true" ondragstart={(e) => onColDragStart(e, col.id)} onclick={(e) => e.stopPropagation()} class="absolute left-1.5 top-1/2 -translate-y-1/2 inline-flex text-[var(--color-text-muted-2)] cursor-grab opacity-0 group-hover/col:opacity-100 transition-opacity z-[1]" title="Перетягнути колонку"><Icon name="grip" size={13} /></span>{/if}
<span class="block truncate align-middle transition-transform duration-150 group-hover/col:translate-x-[15px]">{col.label} <span class="text-[var(--color-pu-3)]">{sortIconCustom(col.fieldKey)}</span></span>
                    <div class="col-resize" role="separator" aria-orientation="vertical" onpointerdown={(e) => startColResize(e, col.fieldKey)}></div>
                  </th>
                {:else if !col.builtIn}
                  <!-- Кастомні текстові/теги/email/... — без sort, лише drag -->
                  <th
                    style={colStyle(col.fieldKey)} class="relative group/col text-left px-3 py-2 font-bold select-none {isDropTarget ? 'bg-[var(--color-pu)]/10 outline-2 outline-dashed outline-[var(--color-pu)]' : ''}"
                    ondragover={(e) => onColDragOver(e, col.id)}
                    ondragleave={onColDragLeave}
                    ondrop={(e) => onColDrop(e, col.id)}
                    title="drag — перемістити колонку"
                  >
                    {#if isAdmin}<span draggable="true" ondragstart={(e) => onColDragStart(e, col.id)} onclick={(e) => e.stopPropagation()} class="absolute left-1.5 top-1/2 -translate-y-1/2 inline-flex text-[var(--color-text-muted-2)] cursor-grab opacity-0 group-hover/col:opacity-100 transition-opacity z-[1]" title="Перетягнути колонку"><Icon name="grip" size={13} /></span>{/if}
<span class="block truncate align-middle transition-transform duration-150 group-hover/col:translate-x-[15px]">{col.label}</span>
                    <div class="col-resize" role="separator" aria-orientation="vertical" onpointerdown={(e) => startColResize(e, col.fieldKey)}></div>
                  </th>
                {:else}
                  <th
                    style={colStyle(col.fieldKey)} class="relative group/col text-left px-3 py-2 font-bold {col.fieldKey === 'displayName' ? 'w-[34%]' : ''} {isDropTarget ? 'bg-[var(--color-pu)]/10 outline-2 outline-dashed outline-[var(--color-pu)]' : ''}"
                    ondragover={(e) => onColDragOver(e, col.id)}
                    ondragleave={onColDragLeave}
                    ondrop={(e) => onColDrop(e, col.id)}
                    title="drag — перемістити колонку"
                  >
                    {#if isAdmin}<span draggable="true" ondragstart={(e) => onColDragStart(e, col.id)} onclick={(e) => e.stopPropagation()} class="absolute left-1.5 top-1/2 -translate-y-1/2 inline-flex text-[var(--color-text-muted-2)] cursor-grab opacity-0 group-hover/col:opacity-100 transition-opacity z-[1]" title="Перетягнути колонку"><Icon name="grip" size={13} /></span>{/if}
<span class="block truncate align-middle transition-transform duration-150 group-hover/col:translate-x-[15px]">{col.label}</span>
                    <div class="col-resize" role="separator" aria-orientation="vertical" onpointerdown={(e) => startColResize(e, col.fieldKey)}></div>
                  </th>
                {/if}
              {/each}
            </tr>
          </thead>
          <tbody>
            {#each items as p (p.id)}
              <tr
                class="border-b border-[var(--color-border-2)] hover:bg-[var(--color-pu)]/[0.04] cursor-pointer transition-colors duration-150"
                class:selected-row={selected.has(p.id)}
                onclick={() => (window.location.hash = `#/persons/${p.id}`)}
              >
                {#if isAdmin}
                  <td class="px-3 py-2.5" onclick={(e) => { e.stopPropagation(); toggleRow(p.id); }}>
                    <input
                      type="checkbox"
                      checked={selected.has(p.id)}
                      onclick={(e) => { e.stopPropagation(); toggleRow(p.id); }}
                    />
                  </td>
                {/if}
                {#each tableColumns as col (col.id)}
                  {#if col.fieldKey === 'displayName'}
                    <td class="px-3 py-2.5">
                      <div class="flex items-center gap-2.5 min-w-0">
                        <Avatar name={p.displayName} />
                        <div class="min-w-0">
                          <div class="font-semibold truncate">{p.displayName ?? '— без імені —'}</div>
                          <div class="text-[10.5px] text-[var(--color-text-muted)] truncate">
                            {p.primaryEmail ?? ''}{p.primaryPhone ? ` · ${p.primaryPhone}` : ''}
                          </div>
                        </div>
                      </div>
                    </td>
                  {:else if col.fieldKey === 'telegramId'}
                    <td class="px-3 py-2.5 text-[11px] font-mono truncate">
                      {#if p.telegramId}
                        <span class="text-[var(--color-text)]">{p.telegramId}</span>
                        {#if p.username}
                          <span class="text-[var(--color-text-muted)]"> @{p.username}</span>
                        {/if}
                      {:else if p.username}
                        <span class="text-[var(--color-text-muted)]">@{p.username}</span>
                      {:else}
                        <span class="text-[var(--color-text-muted-2)]">—</span>
                      {/if}
                    </td>
                  {:else if col.fieldKey === 'currentSubscriptionStatus'}
                    <td class="px-3 py-2.5">
                      <CellEdit
                        field={col}
                        value={p.currentSubscriptionStatus}
                        editable={isPersonCellEditable(col)}
                        onSave={(v) => persistPersonCell(p.id, col, v)}
                      >
                        {#snippet display()}<StatusBadge status={p.currentSubscriptionStatus} />{/snippet}
                      </CellEdit>
                    </td>
                  {:else if col.fieldKey === 'currentTariff'}
                    <td class="px-3 py-2.5 text-xs">
                      <CellEdit
                        field={col}
                        value={p.currentTariff}
                        editable={isPersonCellEditable(col)}
                        onSave={(v) => persistPersonCell(p.id, col, v)}
                      />
                    </td>
                  {:else if col.fieldKey === 'ltvCents'}
                    <td class="px-3 py-2.5 font-semibold tabular-nums">{fmtUah(p.ltvCents)}</td>
                  {:else if col.fieldKey === 'paymentCount'}
                    <td
                      class="px-3 py-2.5 font-semibold tabular-nums"
                      onclick={(e) => {
                        if (p.paymentCount > 0) {
                          e.stopPropagation();
                          window.location.hash = `#/persons/${p.id}?tab=payments`;
                        }
                      }}
                    >
                      {#if p.paymentCount > 0}
                        <span class="text-[var(--color-pu)] hover:underline cursor-pointer" title="Відкрити список платежів">{fmtInt(p.paymentCount)}</span>
                      {:else}
                        <span class="text-[var(--color-text-muted-2)]">0</span>
                      {/if}
                    </td>
                  {:else if col.fieldKey === 'firstSeenAt'}
                    <td class="px-3 py-2.5 text-[11px] text-[var(--color-text-muted)]" title={p.firstSeenAt}>{fmtRelative(p.firstSeenAt)}</td>
                  {:else if col.fieldKey === 'lastSeenAt'}
                    <td class="px-3 py-2.5 text-[11px] text-[var(--color-text-muted)]" title={p.lastSeenAt}>{fmtRelative(p.lastSeenAt)}</td>
                  {:else if col.fieldKey === 'subscriptionEndsAt'}
                    <td class="px-3 py-2.5 text-[11px]">
                      <CellEdit
                        field={col}
                        value={p.subscriptionEndsAt}
                        editable={isPersonCellEditable(col)}
                        onSave={(v) => persistPersonCell(p.id, col, v)}
                      />
                    </td>
                  {:else if col.builtIn}
                    <!-- Інші built-in поля (username/primaryEmail/...) — generic + popover-edit якщо дозволено. -->
                    <td class="px-3 py-2.5 text-xs">
                      <CellEdit
                        field={col}
                        value={(p as unknown as Record<string, unknown>)[col.fieldKey]}
                        editable={isPersonCellEditable(col)}
                        onSave={(v) => persistPersonCell(p.id, col, v)}
                      />
                    </td>
                  {:else}
                    <!-- Custom field — popover-редагування (formula/computed лише читаються). -->
                    <td class="px-3 py-2.5 text-xs">
                      <CellEdit
                        field={col}
                        value={p.customFields?.[col.fieldKey]}
                        record={{ ...p, ...(p.customFields ?? {}) }}
                        editable={isPersonCellEditable(col)}
                        onSave={(v) => persistPersonCell(p.id, col, v)}
                      />
                    </td>
                  {/if}
                {/each}
              </tr>
            {/each}
          </tbody>
        </table>
        </div>
        <div class="border-t border-[var(--color-border-3)] bg-[var(--color-bg-3)] px-3.5 py-2.5">
          <div class="text-[11px] text-[var(--color-text-muted)] mb-2">
            Показано <strong>{items.length}</strong> з <strong>{fmtInt(total)}</strong> ·
            {#if sort.startsWith('ltv')}LTV {sort.endsWith('Desc') ? '↓' : '↑'}
            {:else if sort.startsWith('payments')}Платежів {sort.endsWith('Desc') ? '↓' : '↑'}
            {:else if sort.startsWith('lastSeen')}Last seen {sort.endsWith('Desc') ? '↓' : '↑'}
            {:else if sort.startsWith('subscriptionEnds')}Підписка до {sort.endsWith('Desc') ? '↓' : '↑'}
            {:else if sort.startsWith('custom:')}↕ {sort.split(':')[1]}
            {:else}First seen {sort.endsWith('Desc') ? '↓' : '↑'}
            {/if}
          </div>
          <Pagination {total} {page} {pageSize} onpage={onPageChange} onpagesize={onPageSizeChange} />
        </div>
      </div>
      {/if}
    {/if}
  </div>

  <FilterBuilder
    open={showBuilder}
    bind:root={draftTree}
    onApply={applyBuilder}
    onCancel={cancelBuilder}
    onClear={clearBuilder}
    savedFilters={savedFilters}
    onSavedChanged={loadSavedFilters}
    currentSort={sort}
    onSavedLoad={(sf) => {
      if (sf.sort && sf.sort !== sort) {
        sort = sf.sort as SortValue;
        page = 1;
        void load();
      }
    }}
    scope="persons"
    customFields={filterFields}
  />

  <BulkEditModal
    open={bulkEditOpen}
    selectedIds={Array.from(selected)}
    editableFields={bulkEditableFields}
    entityLabel={selected.size === 1 ? 'контакту' : 'контактів'}
    onApply={applyBulkEditToOne}
    onClose={() => (bulkEditOpen = false)}
    onDone={onBulkEditDone}
  />

  <!-- Delete confirmation modal -->

  {#if confirmOpen}
    <div
      class="fixed inset-0 z-40 bg-black/40 flex items-center justify-center"
      onclick={() => !confirmBusy && (confirmOpen = false)}
      role="presentation"
    >
      <div
        class="w-[min(440px,90vw)] rounded-xl bg-white shadow-2xl p-5"
        onclick={(e) => e.stopPropagation()}
        role="dialog"
      >
        <div class="flex items-center gap-2 text-sm font-bold mb-2"><Icon name="delete" size={16} /> Видалити контактів?</div>
        <div class="text-xs text-[var(--color-text-muted)] mb-4">
          Буде назавжди видалено <strong class="text-[var(--color-text)]">{selected.size}</strong>
          {selected.size === 1 ? 'контакт' : 'контактів'} разом з його контактними даними
          (email / телефон / telegram).
          <br /><br />
          Платежі, підписки, сесії перегляду та пов'язані картки-угоди
          <strong class="text-[var(--color-text)]">зберігаються</strong> — вони просто
          стануть «без контакту». Цю дію не можна скасувати.
        </div>
        {#if confirmErr}
          <div class="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-700">{confirmErr}</div>
        {/if}
        <div class="flex justify-end gap-2">
          <button
            onclick={() => (confirmOpen = false)}
            disabled={confirmBusy}
            class="rounded-md border border-[var(--color-border-3)] px-3 py-1.5 text-xs font-medium"
          >Скасувати</button>
          <button
            onclick={doDelete}
            disabled={confirmBusy}
            class="rounded-md bg-red-600 text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
          >{confirmBusy ? 'Видаляю…' : 'Видалити назавжди'}</button>
        </div>
      </div>
    </div>
  {/if}

  {#if createOpen}
    <div class="fixed inset-0 z-50 grid place-items-center bg-black/30 p-4" onclick={() => (createOpen = false)}>
      <form
        onsubmit={submitCreatePerson}
        onclick={(e) => e.stopPropagation()}
        class="w-full max-w-lg rounded-xl border border-[var(--color-border-3)] bg-white shadow-xl"
      >
        <div class="flex items-center justify-between border-b border-[var(--color-border-3)] px-5 py-3">
          <h3 class="text-base font-bold">Новий запис</h3>
          <button type="button" onclick={() => (createOpen = false)} class="inline-flex items-center text-[var(--color-text-muted)] hover:text-[var(--color-text)]"><Icon name="close" size={16} /></button>
        </div>

        <div class="px-5 py-4 space-y-3">
          <label class="block">
            <div class="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Імʼя</div>
            <input bind:value={nDisplayName} placeholder="Іван Іваненко"
              class="w-full rounded-md border border-[var(--color-border-3)] px-3 py-2 text-sm" />
          </label>
          <label class="block">
            <div class="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Telegram username</div>
            <input bind:value={nUsername} placeholder="ivanenko"
              class="w-full rounded-md border border-[var(--color-border-3)] px-3 py-2 text-sm" />
          </label>
          <label class="block">
            <div class="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Email</div>
            <input bind:value={nEmail} type="email" placeholder="ivan@example.com"
              class="w-full rounded-md border border-[var(--color-border-3)] px-3 py-2 text-sm" />
          </label>
          <label class="block">
            <div class="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Телефон</div>
            <input bind:value={nPhone} type="tel" placeholder="+380501234567"
              class="w-full rounded-md border border-[var(--color-border-3)] px-3 py-2 text-sm" />
          </label>
          <label class="block">
            <div class="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Telegram ID</div>
            <input bind:value={nTelegramId} placeholder="123456789"
              class="w-full rounded-md border border-[var(--color-border-3)] px-3 py-2 text-sm" />
          </label>

          <p class="text-[11px] text-[var(--color-text-muted-2)]">
            Якщо email / phone / telegram_id уже існує у системі —
            відкриється існуючий профіль (без дубліката).
          </p>

          {#if createErr}
            <div class="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-700">{createErr}</div>
          {/if}
        </div>

        <div class="flex justify-end gap-2 border-t border-[var(--color-border-3)] px-5 py-3">
          <button
            type="button"
            onclick={() => (createOpen = false)}
            class="rounded-md border border-[var(--color-border-3)] bg-white px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] hover:bg-[var(--color-bg-3)]"
          >
            Скасувати
          </button>
          <button
            type="submit"
            disabled={createBusy}
            class="rounded-md bg-[var(--color-pu)] px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white disabled:opacity-50"
          >
            {createBusy ? 'Створення…' : 'Створити'}
          </button>
        </div>
      </form>
    </div>
  {/if}
</AppShell>

{#if cardConfigOpen}
  <KanbanCardConfigModal
    slug="users"
    availableFields={cardConfigFields}
    config={cardConfig}
    sampleValues={items[0] ? personToValues(items[0]) : {}}
    onSave={saveCard}
    onClose={() => (cardConfigOpen = false)}
  />
{/if}

<style>
  /* Вибраний рядок таблиці — м'який фіолетовий + акцентна планка зліва. */
  tr.selected-row {
    background: var(--color-pu-light);
    box-shadow: inset 2px 0 0 var(--color-pu);
  }
</style>

